import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment
} from '@mui/material';
import {
    Send as SendIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon,
    PersonSearch as PersonSearchIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, TORQUE_MAP_STORE, EMPLOYEE_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import torqueMapService from '../../../services/torqueMapService';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const TorqueMapForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        customer: '',
        location: '',
        torqueItems: [{
            itemNo: 1,
            date: '',
            torqueNo: '',
            drawingNo: '',
            torqueValue: '',
            employee: '',
            inspectorType: 'employee',
            inspector: '',
            inspectionDate: ''
        }],
        signatureType: 'internal',
        internalEmployeeId: userId,
        internalEmployeeName: userFullName,
        internalSignature: '',
        internalSignatureDate: dayjs().format('YYYY-MM-DD'),
        clientName: '',
        clientSignature: '',
        clientSignatureDate: dayjs().format('YYYY-MM-DD'),
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [signatureEmployeeModalOpen, setSignatureEmployeeModalOpen] = useState(false);
    const [inspectorModalOpen, setInspectorModalOpen] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(null);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            label: `${job.Job} - ${job.JobName}`
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    // Load jobs, employees and form data on mount
    useEffect(() => {
        const loadData = async () => {
            const db = await initializeDB();

            // Load jobs
            if (db.objectStoreNames.contains(JOB_STORE)) {
                const tx = db.transaction(JOB_STORE, 'readonly');
                const store = tx.objectStore(JOB_STORE);
                const jobsData = await store.getAll();
                setJobs(jobsData || []);
            }

            // Load employees
            if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                const store = tx.objectStore(EMPLOYEE_STORE);
                const allEmployees = await store.getAll();
                setEmployees(allEmployees || []);
            }

            if (uuid) {
                const existingForm = await torqueMapService.loadByUuid(uuid);
                if (existingForm) {
                    setForm(existingForm);
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);
                }
            }
        };
        loadData();
    }, [uuid]);

    // Queue save with debounce
    const queueSave = (formData) => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        saveTimer.current = setTimeout(() => {
            saveDraft(formData);
        }, 1000);
    };

    // Save draft to IndexedDB
    const saveDraft = async (formData) => {
        try {
            await torqueMapService.saveDraft(formData);
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    // Handle field change
    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Handle job selection
    const handleJobChange = (event, newValue) => {
        if (!newValue) {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                customer: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
            return;
        }

        const updated = {
            ...form,
            jobCode: newValue.Job || '',
            jobName: newValue.JobName || '',
            customer: newValue.Customer || '',
            location: newValue.Location || newValue.location || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle signature type change
    const handleSignatureTypeChange = (event) => {
        const newType = event.target.value;
        const updated = {
            ...form,
            signatureType: newType
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle signature accept
    const handleSignatureAccept = () => {
        if (!sigPadRef.current) {
            console.error('Signature pad ref not found');
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const dateField = form.signatureType === 'internal' ? 'internalSignatureDate' : 'clientSignatureDate';
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: dataUrl,
            [dateField]: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    // Clear signature
    const handleClearSignature = () => {
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle select internal employee
    const handleSelectInternalEmployee = (person) => {
        const updated = {
            ...form,
            internalEmployeeId: person.id || '',
            internalEmployeeName: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setSignatureEmployeeModalOpen(false);
    };

    // Handle select inspector
    const handleSelectInspector = (person) => {
        if (currentItemIndex !== null) {
            const updated = {
                ...form,
                torqueItems: form.torqueItems.map((item, idx) =>
                    idx === currentItemIndex
                        ? { ...item, inspector: person.FullName || person.name || '' }
                        : item
                )
            };
            setForm(updated);
            queueSave(updated);
        }
        setInspectorModalOpen(false);
        setCurrentItemIndex(null);
    };

    // Handle select employee
    const handleSelectEmployee = (person) => {
        if (currentItemIndex !== null) {
            const updated = {
                ...form,
                torqueItems: form.torqueItems.map((item, idx) =>
                    idx === currentItemIndex
                        ? { ...item, employee: person.FullName || person.name || '' }
                        : item
                )
            };
            setForm(updated);
            queueSave(updated);
        }
        setEmployeeModalOpen(false);
        setCurrentItemIndex(null);
    };

    // Add weld item
    const handleAddItem = () => {
        const newItem = {
            id: generateUUID(),
            itemNo: form.torqueItems.length + 1,
            date: '',
            torqueNo: '',
            drawingNo: '',
            torqueValue: '',
            employee: '',
            inspectorType: 'employee',
            inspector: '',
            inspectionDate: ''
        };
        const updated = {
            ...form,
            torqueItems: [...form.torqueItems, newItem]
        };
        setForm(updated);
        queueSave(updated);
    };

    // Update weld item
    const handleItemChange = (index, field, value) => {
        const updated = { ...form };
        updated.torqueItems[index][field] = value;
        setForm(updated);
        queueSave(updated);
    };

    // Delete weld item
    const handleDeleteItem = (index) => {
        const updated = { ...form };
        updated.torqueItems.splice(index, 1);
        // Re-number items
        updated.torqueItems.forEach((item, idx) => {
            item.itemNo = idx + 1;
        });
        setForm(updated);
        queueSave(updated);
    };

    // Duplicate weld item
    const handleDuplicateItem = (index) => {
        const itemToDuplicate = form.torqueItems[index];
        const newItem = {
            ...itemToDuplicate,
            id: generateUUID(),
            itemNo: form.torqueItems.length + 1
        };
        const updated = {
            ...form,
            torqueItems: [...form.torqueItems, newItem]
        };
        setForm(updated);
        queueSave(updated);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!form.jobCode) {
            newErrors.jobCode = 'Job is required';
        }

        // Validate torque items
        form.torqueItems.forEach((item, index) => {
            if (!item.date) {
                newErrors[`item_${index}_date`] = 'Date is required';
            }
        });

        // Validate signatures
        if (form.signatureType === 'internal') {
            if (!form.internalSignature) newErrors.internalSignature = 'Signature is required';
            if (!form.internalEmployeeName) newErrors.internalEmployeeName = 'Employee name is required';
        } else {
            if (!form.clientSignature) newErrors.clientSignature = 'Signature is required';
            if (!form.clientName) newErrors.clientName = 'Client/Inspector name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle submit
    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Form?',
            text: 'Once submitted, the form cannot be edited',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            const submitData = { ...form, status: STATUS_SUBMITTED };
            await torqueMapService.submit(submitData);

            // Delete from IndexedDB after successful submission
            await torqueMapService.delete(form.uuid);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Torque Map form submitted successfully'
            });

            navigate('/piping/forms/torque-map/list');
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit form'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/piping/forms/torque-map/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Torque Map</Typography>
                    </Box>
                    <Chip
                        label={submitted ? 'Submitted' : 'Draft'}
                        size="small"
                        sx={{
                            backgroundColor: submitted ? '#4caf50' : '#9e9e9e',
                            color: 'white'
                        }}
                    />
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Job Information */}
                    <Accordion defaultExpanded sx={{
                        mb: 0.5,
                        '&.Mui-expanded': {
                            margin: '0 0 4px 0'
                        }
                    }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={sortedJobs}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                        onChange={handleJobChange}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Job *"
                                                error={!!errors.jobCode}
                                                helperText={errors.jobCode}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Customer"
                                        value={form.customer || ''}
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        disabled={submitted}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Torque Items */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Torque Items</Typography>
                            </Box>
                            {form.torqueItems.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No torque items added yet. Click "Add Item" to get started.
                                </Typography>
                            ) : (
                                <Stack spacing={1.25}>
                                    {form.torqueItems.map((item, index) => (
                                        <Card key={index} variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        Item #{item.itemNo}
                                                    </Typography>
                                                    {!submitted && (
                                                        <Box display="flex" gap={1.5}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDuplicateItem(index)}
                                                                color="primary"
                                                                title="Duplicate item"
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteItem(index)}
                                                                color="error"
                                                                title="Delete item"
                                                                disabled={form.torqueItems.length === 1}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                                    {/* Row 1: Date + Employee */}
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="date"
                                                            label="Date *"
                                                            value={item.date}
                                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                            onChange={(e) => handleItemChange(index, 'date', e.target.value)}
                                                            disabled={submitted}
                                                            error={!!errors[`item_${index}_date`]}
                                                            helperText={errors[`item_${index}_date`]}
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Employee"
                                                            value={item.employee}
                                                            onClick={() => {
                                                                if (!submitted) {
                                                                    setCurrentItemIndex(index);
                                                                    setEmployeeModalOpen(true);
                                                                }
                                                            }}
                                                            InputProps={{
                                                                readOnly: true,
                                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                                endAdornment: !submitted && (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                setCurrentItemIndex(index);
                                                                                setEmployeeModalOpen(true);
                                                                            }}
                                                                            size="small"
                                                                        >
                                                                            <PersonSearchIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>

                                                    {/* Row 2: Inspector + Inspection Date */}
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label={item.inspectorType === 'employee' ? 'Inspector (Employee)' : 'Inspector (Client)'}
                                                            value={item.inspector}
                                                            onClick={() => {
                                                                if (!submitted && item.inspectorType === 'employee') {
                                                                    setCurrentItemIndex(index);
                                                                    setInspectorModalOpen(true);
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                if (item.inspectorType === 'client') {
                                                                    handleItemChange(index, 'inspector', e.target.value);
                                                                }
                                                            }}
                                                            InputProps={{
                                                                readOnly: item.inspectorType === 'employee',
                                                                style: { cursor: submitted ? 'default' : (item.inspectorType === 'employee' ? 'pointer' : 'text') },
                                                                startAdornment: !submitted && (
                                                                    <InputAdornment position="start" onClick={(e) => e.stopPropagation()}>
                                                                        <ToggleButtonGroup
                                                                            value={item.inspectorType}
                                                                            exclusive
                                                                            onChange={(e, newValue) => {
                                                                                e.stopPropagation();
                                                                                if (newValue && newValue !== item.inspectorType) {
                                                                                    const updated = {
                                                                                        ...form,
                                                                                        torqueItems: form.torqueItems.map((it, idx) =>
                                                                                            idx === index
                                                                                                ? { ...it, inspectorType: newValue, inspector: '' }
                                                                                                : it
                                                                                        )
                                                                                    };
                                                                                    setForm(updated);
                                                                                    queueSave(updated);
                                                                                }
                                                                            }}
                                                                            size="small"
                                                                            sx={{ height: 24 }}
                                                                        >
                                                                            <ToggleButton value="employee" sx={{ px: 0.75, py: 0.25, fontSize: '0.7rem' }}>
                                                                                Emp
                                                                            </ToggleButton>
                                                                            <ToggleButton value="client" sx={{ px: 0.75, py: 0.25, fontSize: '0.7rem' }}>
                                                                                Client
                                                                            </ToggleButton>
                                                                        </ToggleButtonGroup>
                                                                    </InputAdornment>
                                                                ),
                                                                endAdornment: !submitted && item.inspectorType === 'employee' && (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                setCurrentItemIndex(index);
                                                                                setInspectorModalOpen(true);
                                                                            }}
                                                                            size="small"
                                                                        >
                                                                            <PersonSearchIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="date"
                                                            label="Inspection Date"
                                                            value={item.inspectionDate}
                                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                            onChange={(e) => handleItemChange(index, 'inspectionDate', e.target.value)}
                                                            disabled={submitted}
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                                            }}
                                                        />
                                                    </Grid>

                                                    {/* Row 3: Torque # + Drawing # + Torque Value */}
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Torque #"
                                                            value={item.torqueNo}
                                                            onChange={(e) => handleItemChange(index, 'torqueNo', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Drawing #"
                                                            value={item.drawingNo}
                                                            onChange={(e) => handleItemChange(index, 'drawingNo', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Torque Value"
                                                            value={item.torqueValue}
                                                            onChange={(e) => handleItemChange(index, 'torqueValue', e.target.value)}
                                                            disabled={submitted}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {!submitted && form.torqueItems.length > 0 && (
                                        <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddItem}
                                                size="small"
                                            >
                                                Add Item
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signature</Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={form.signatureType}
                                    exclusive
                                    onChange={(e, newValue) => newValue && handleSignatureTypeChange({ target: { value: newValue } })}
                                    disabled={submitted}
                                    size="small"
                                >
                                    <ToggleButton value="internal" sx={{ px: 2 }}>
                                        Internal
                                    </ToggleButton>
                                    <ToggleButton value="client" sx={{ px: 2 }}>
                                        Client/Inspector
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {form.signatureType === 'internal' ? (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Employee Name *"
                                                value={form.internalEmployeeName}
                                                onClick={() => !submitted && setSignatureEmployeeModalOpen(true)}
                                                InputProps={{
                                                    readOnly: true,
                                                    style: { cursor: submitted ? 'default' : 'pointer' },
                                                    endAdornment: !submitted && (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setSignatureEmployeeModalOpen(true)} size="small">
                                                                <PersonSearchIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.internalEmployeeName}
                                                helperText={errors.internalEmployeeName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.internalSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('internalSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.internalSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.internalSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.internalSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.internalSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Client/Inspector Name *"
                                                value={form.clientName}
                                                onChange={(e) => handleFieldChange('clientName', e.target.value)}
                                                disabled={submitted}
                                                error={!!errors.clientName}
                                                helperText={errors.clientName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.clientSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('clientSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.clientSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.clientSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.clientSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.clientSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.signatureType === 'internal' ? form.internalEmployeeName : form.clientName}
                onAccept={handleSignatureAccept}
                existingSignature={form.signatureType === 'internal' ? form.internalSignature : form.clientSignature}
            />

            {/* Employee Selection Modal */}
            <SelectSingleEmployeeModal
                open={signatureEmployeeModalOpen}
                onClose={() => setSignatureEmployeeModalOpen(false)}
                onSelect={handleSelectInternalEmployee}
                employees={employees}
            />

            {/* Inspector Selection Modal */}
            <SelectSingleEmployeeModal
                open={inspectorModalOpen}
                onClose={() => {
                    setInspectorModalOpen(false);
                    setCurrentItemIndex(null);
                }}
                onSelect={handleSelectInspector}
                employees={employees}
            />

            {/* Employee Selection Modal */}
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => {
                    setEmployeeModalOpen(false);
                    setCurrentItemIndex(null);
                }}
                onSelect={handleSelectEmployee}
                employees={employees}
            />

            <Backdrop open={saving} sx={{ color: '#fff', zIndex: 9999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default TorqueMapForm;
